import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import './StudentNotificationsView.css';

function StudentNotificationsView({ student }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setNotifications([]);

      const notificationsRef = collection(db, 'notifications');
      const q = query(notificationsRef, where('studentId', '==', student.studentId));
      const snapshot = await getDocs(q);
      const notificationsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Sort by date (newest first)
      notificationsData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setNotifications(notificationsData);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [student.studentId]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, { isRead: true });
      setNotifications(notifications.map(n =>
        n.id === notificationId ? { ...n, isRead: true } : n
      ));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      'assignment': '📝',
      'online_class': '📹',
      'learning_material': '📚',
      'grade': '📊',
      'event': '🎉',
      'general': '🔔',
    };
    return icons[type] || '🔔';
  };

  return (
    <div className="student-notifications-view">
      <h2>Notifications</h2>

      {loading ? (
        <div className="loading">Loading notifications...</div>
      ) : notifications.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🔔</div>
          <h3>No Notifications</h3>
          <p>You're all caught up!</p>
        </div>
      ) : (
        <div className="notifications-list">
          {notifications.map(notification => (
            <div
              key={notification.id}
              className={`notification-card ${notification.isRead ? 'read' : 'unread'}`}
              onClick={() => !notification.isRead && markAsRead(notification.id)}
            >
              <div className="notification-header">
                <span className="notification-icon">
                  {getNotificationIcon(notification.type)}
                </span>
                <span className="notification-title">{notification.title}</span>
                {!notification.isRead && <span className="unread-badge">New</span>}
              </div>
              <p className="notification-message">{notification.message}</p>
              <div className="notification-meta">
                {notification.createdAt && (
                  <span>{new Date(notification.createdAt).toLocaleString()}</span>
                )}
              </div>
              {notification.actionUrl && (
                <a
                  href={notification.actionUrl}
                  className="action-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  View Details
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default StudentNotificationsView;
